import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter, useNavigate } from 'react-router-dom'
import { ConfigProvider, App as AntApp, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { MsalProvider } from '@azure/msal-react'
import App from './App.jsx'
import store from './store/store.js'
import { msalInstance } from './services/msalAuthService.js'
import { setUser, clearUser } from './features/auth/authSlice.js'
import authService from './api/authService.js'
import './index.css'

// Process an MSAL AuthenticationResult by exchanging the Azure access token
// with the backend and seeding the Redux store. Used by the redirect-flow
// bootstrap so the app renders with the user already signed in.
const processMsalResponse = async (response) => {
  if (!response || !response.accessToken) return { ok: false, navigateTo: null }
  try {
    msalInstance.setActiveAccount(response.account)
    const apiResponse = await authService.loginUserAzure(response.accessToken)
    if (
      apiResponse?.requestIsSuccess &&
      apiResponse?.isAuthenticated &&
      apiResponse?.userHasAccess
    ) {
      if (apiResponse.tokenID) {
        localStorage.setItem('access_token', apiResponse.tokenID)
      }
      store.dispatch(
        setUser({
          username: apiResponse.userEmailID || response.account.username,
          name: apiResponse.fullName || response.account.name,
          email: apiResponse.userEmailID || response.account.username,
          id: apiResponse.userGPN || response.account.homeAccountId,
          token: apiResponse.tokenID,
          authType: 'microsoft',
          role: apiResponse.userCurrentRole,
          roles: apiResponse.userAppRoles,
        })
      )
      return { ok: true, navigateTo: '/dashboard' }
    }

    // Authenticated with AAD but backend denied access
    localStorage.removeItem('access_token')
    localStorage.removeItem('user')
    store.dispatch(clearUser())
    return {
      ok: false,
      navigateTo: '/unauthorized',
      message:
        apiResponse?.requestMessage ||
        'User is not authenticated or does not have access to this application',
    }
  } catch (err) {
    console.error('Backend login after redirect failed:', err)
    const status = err?.response?.status
    // Backend denied access (401/403) - route to the access denied page
    if (status === 401 || status === 403) {
      localStorage.removeItem('access_token')
      localStorage.removeItem('user')
      store.dispatch(clearUser())
      return {
        ok: false,
        navigateTo: '/unauthorized',
        message:
          err?.response?.data?.requestMessage ||
          err?.response?.data?.message ||
          'User is not authenticated or does not have access to this application',
      }
    }
    return { ok: false, navigateTo: '/login' }
  }
}

// Remove the pre-mount HTML loader (added in index.html). Called once React
// has rendered enough that we want the bootstrap spinner to disappear.
const removeHtmlBootstrapLoader = () => {
  const el = document.getElementById('app-bootstrap-loader')
  if (el && el.parentNode) {
    el.parentNode.removeChild(el)
  }
}

// Full-screen in-React loader shown while MSAL completes a redirect response.
const FullScreenLoader = ({ message = 'Signing you in...' }) => (
  <div
    style={{
      position: 'fixed',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#fff',
      zIndex: 9999,
    }}
  >
    <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} size="large" />
    <div style={{ marginTop: 20, fontSize: 16, fontWeight: 500 }}>Application Tracker</div>
    <div style={{ marginTop: 6, fontSize: 13, color: '#666' }}>{message}</div>
  </div>
)

// Top-level component that runs MSAL handleRedirectPromise on mount.
// Renders a loader until bootstrap completes, then renders <App />.
// Lives inside <BrowserRouter> so it can call useNavigate.
const AuthBootstrap = ({ children }) => {
  const [ready, setReady] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      try {
        const response = await msalInstance.handleRedirectPromise()
        if (cancelled) return

        if (response) {
          const result = await processMsalResponse(response)
          if (cancelled) return
          if (result.navigateTo) {
            navigate(result.navigateTo, {
              replace: true,
              state: result.message ? { message: result.message } : undefined,
            })
          }
        } else {
          // No redirect response - if there's a cached account, mark it active
          const accounts = msalInstance.getAllAccounts()
          if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
            msalInstance.setActiveAccount(accounts[0])
          }
        }
      } catch (err) {
        console.error('handleRedirectPromise failed:', err)
      } finally {
        if (!cancelled) {
          setReady(true)
          // Remove the pre-mount HTML loader if it's still around
          removeHtmlBootstrapLoader()
        }
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [navigate])

  if (!ready) {
    return <FullScreenLoader message="Signing you in..." />
  }
  return children
}

// Bootstrap: only initialize() runs before render so MsalProvider receives an
// initialized instance. Everything else (handleRedirectPromise, backend login)
// happens inside <AuthBootstrap>, which shows a loader until done.
const bootstrap = async () => {
  try {
    await msalInstance.initialize()
  } catch (err) {
    console.error('MSAL initialize failed:', err)
  }

  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <Provider store={store}>
        <MsalProvider instance={msalInstance}>
          {/* <BrowserRouter basename="/apptracker/"> */}
           <BrowserRouter basename="/">
            <ConfigProvider
              theme={{
                token: {
                  colorPrimary: '#1890ff',
                  borderRadius: 4,
                },
              }}
            >
              <AntApp>
                <AuthBootstrap>
                  <App />
                </AuthBootstrap>
              </AntApp>
            </ConfigProvider>
          </BrowserRouter>
        </MsalProvider>
      </Provider>
    </React.StrictMode>,
  )
}

bootstrap()
